import React, { useState, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Plus, 
  Clock, 
  Calendar, 
  User, 
  AlertCircle, 
  CheckCircle2,
  PlayCircle,
  PauseCircle
} from 'lucide-react';
import { Task, TaskStatus, TaskPriority } from '@/types';
import { CreateTaskDialog } from '@/components/CreateTaskDialog';

export const KanbanBoard: React.FC = () => {
  const { currentProject, getProjectTasks, updateTask } = useProject();
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);

  const tasks = currentProject ? getProjectTasks(currentProject.id) : [];

  const columns = useMemo(() => [
    {
      id: 'todo',
      title: 'A Fazer',
      status: TaskStatus.TODO,
      color: 'bg-gray-50 border-gray-200',
      headerColor: 'bg-gray-100',
      icon: <AlertCircle className="w-4 h-4 text-gray-500" />
    },
    {
      id: 'in_progress',
      title: 'Em Andamento',
      status: TaskStatus.IN_PROGRESS,
      color: 'bg-blue-50 border-blue-200',
      headerColor: 'bg-blue-100',
      icon: <PlayCircle className="w-4 h-4 text-blue-500" />
    },
    {
      id: 'review',
      title: 'Em Revisão',
      status: TaskStatus.REVIEW,
      color: 'bg-yellow-50 border-yellow-200',
      headerColor: 'bg-yellow-100',
      icon: <PauseCircle className="w-4 h-4 text-yellow-500" />
    },
    {
      id: 'done',
      title: 'Concluído',
      status: TaskStatus.DONE,
      color: 'bg-green-50 border-green-200',
      headerColor: 'bg-green-100',
      icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
    }
  ], []);

  const tasksByStatus = useMemo(() => {
    const grouped = tasks.reduce((acc, task) => {
      if (!acc[task.status]) {
        acc[task.status] = [];
      }
      acc[task.status].push(task);
      return acc;
    }, {} as Record<TaskStatus, Task[]>);

    // Sort tasks by priority and creation date
    Object.values(TaskStatus).forEach(status => {
      if (grouped[status]) {
        grouped[status].sort((a, b) => {
          const priorityOrder = { 'urgent': 4, 'high': 3, 'medium': 2, 'low': 1 };
          const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
          if (priorityDiff !== 0) return priorityDiff;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
      }
    });

    return grouped;
  }, [tasks]);

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT: return 'bg-red-100 text-red-800 border-red-300';
      case TaskPriority.HIGH: return 'bg-orange-100 text-orange-800 border-orange-300';
      case TaskPriority.MEDIUM: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case TaskPriority.LOW: return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityText = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT: return 'Urgente';
      case TaskPriority.HIGH: return 'Alta';
      case TaskPriority.MEDIUM: return 'Média';
      case TaskPriority.LOW: return 'Baixa';
      default: return priority;
    }
  };

  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  const getUserById = (userId: string) => mockUsers.find(u => u.id === userId);

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, newStatus: TaskStatus) => {
    e.preventDefault();
    if (draggedTask && draggedTask.status !== newStatus) {
      updateTask(draggedTask.id, { status: newStatus });
      console.log(`Task ${draggedTask.id} moved from ${draggedTask.status} to ${newStatus}`);
    }
    setDraggedTask(null);
  };

  const isOverdue = (task: Task) => {
    return new Date(task.endDate) < new Date() && task.status !== TaskStatus.DONE;
  };

  const getDaysUntilDeadline = (task: Task) => {
    const today = new Date();
    const deadline = new Date(task.endDate);
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!currentProject) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum projeto selecionado</h3>
          <p className="text-gray-600">Selecione um projeto na lista de projetos para visualizar o quadro Kanban</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{currentProject.name}</h1>
          <p className="text-gray-600">{currentProject.description}</p>
        </div>
        <Button onClick={() => setIsCreateTaskOpen(true)} className="bg-gradient-to-r from-blue-500 to-purple-600">
          <Plus className="w-4 h-4 mr-2" />
          Nova Tarefa
        </Button>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-6 min-w-full pb-6">
          {columns.map(column => {
            const columnTasks = tasksByStatus[column.status] || [];
            
            return (
              <div
                key={column.id}
                className={`flex-1 min-w-80 ${column.color} border-2 border-dashed rounded-lg p-4`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.status)}
              >
                {/* Column Header */}
                <div className={`${column.headerColor} rounded-lg p-3 mb-4`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {column.icon}
                      <h3 className="font-semibold text-gray-900">{column.title}</h3>
                    </div>
                    <Badge variant="secondary" className="bg-white/50">
                      {columnTasks.length}
                    </Badge>
                  </div>
                </div>

                {/* Tasks */}
                <div className="space-y-3">
                  {columnTasks.map(task => {
                    const assignedUser = task.assignedTo ? getUserById(task.assignedTo) : null;
                    const daysUntilDeadline = getDaysUntilDeadline(task);
                    const isTaskOverdue = isOverdue(task);

                    return (
                      <Card
                        key={task.id}
                        className="cursor-move hover:shadow-md transition-shadow bg-white border-l-4 border-l-blue-500"
                        draggable
                        onDragStart={(e) => handleDragStart(e, task)}
                      >
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Task Header */}
                            <div className="flex items-start justify-between">
                              <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                {task.title}
                              </h4>
                              <Badge variant="secondary" className={`${getPriorityColor(task.priority)} text-xs`}>
                                {getPriorityText(task.priority)}
                              </Badge>
                            </div>

                            {/* Description */}
                            {task.description && (
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {task.description}
                              </p>
                            )}

                            {/* Tags */}
                            {task.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {task.tags.slice(0, 3).map(tag => (
                                  <Badge key={tag} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                                {task.tags.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{task.tags.length - 3}
                                  </Badge>
                                )}
                              </div>
                            )}

                            {/* Progress Bar */}
                            {task.progress > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs text-gray-600">
                                  <span>Progresso</span>
                                  <span>{task.progress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${task.progress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Bottom Row */}
                            <div className="flex items-center justify-between">
                              {/* Assignee */}
                              <div className="flex items-center space-x-2">
                                {assignedUser ? (
                                  <Avatar className="w-5 h-5">
                                    <AvatarImage src={assignedUser.avatar} alt={assignedUser.name} />
                                    <AvatarFallback className="text-xs">
                                      {assignedUser.name.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                ) : (
                                  <User className="w-4 h-4 text-gray-400" />
                                )}
                                <span className="text-xs text-gray-600">
                                  {assignedUser ? assignedUser.name : 'Não atribuído'}
                                </span>
                              </div>

                              {/* Deadline */}
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3 text-gray-400" />
                                <span className={`text-xs ${
                                  isTaskOverdue ? 'text-red-600 font-medium' : 
                                  daysUntilDeadline <= 2 ? 'text-yellow-600 font-medium' : 
                                  'text-gray-600'
                                }`}>
                                  {isTaskOverdue ? 'Atrasado' : 
                                   daysUntilDeadline === 0 ? 'Hoje' : 
                                   daysUntilDeadline === 1 ? 'Amanhã' : 
                                   `${daysUntilDeadline}d`}
                                </span>
                              </div>
                            </div>

                            {/* Estimated vs Actual Hours */}
                            <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-3 h-3" />
                                <span>Est: {task.estimatedHours}h</span>
                              </div>
                              {task.actualHours && (
                                <span>Real: {task.actualHours}h</span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {/* Empty State */}
                  {columnTasks.length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-sm">
                        Nenhuma tarefa em {column.title.toLowerCase()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <CreateTaskDialog 
        open={isCreateTaskOpen}
        onOpenChange={setIsCreateTaskOpen}
        projectId={currentProject.id}
      />
    </div>
  );
};